import React from "react";
import {
  Box,
  Dialog as MuiDialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Close, Email } from "@mui/icons-material";
import dayjs from "dayjs";
import { Guest } from "src/types";
import { useMeetingContactEmails } from "src/queries/useMeetingContactEmails";
import RichText from "../RichText";

export interface WebinarEmailHistoryModalProps {
  open: boolean;
  onClose: () => void;
  meetingId: number;
  guest: Guest;
}

export function WebinarEmailHistoryModal({
  open,
  onClose,
  meetingId,
  guest,
}: WebinarEmailHistoryModalProps) {
  const { data, loading, error } = useMeetingContactEmails(meetingId, guest.id);

  // Get emails array safely - API returns data.data array
  const emails = data?.data || [];

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: "hidden", padding: 0 },
      }}
    >
      <Box
        sx={{
          backgroundColor: "#1D4971",
          color: "white",
          py: 2.5,
          px: 3,
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Email />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Promotion Email History
          </Typography>
          <Typography variant="body2">
            {guest.firstName} {guest.lastName} ({guest.email})
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "white",
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            Failed to load email history. Please try again.
          </Alert>
        )}

        {!loading && !error && (
          <>
            {emails.length === 0 ? (
              <Alert severity="info" icon={<Email />}>
                No emails have been sent to this guest yet.
              </Alert>
            ) : (
              <Stack spacing={3}>
                <Typography variant="body2" color="text.secondary">
                  {emails.length} email{emails.length !== 1 ? "s" : ""} sent
                </Typography>

                {emails.map((email, index) => (
                  <Box key={index}>
                    <Stack spacing={2}>
                      <Box>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={2}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            {email.subject || "(No Subject)"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {dayjs(email.createdAt).format(
                              "MMM D, YYYY h:mm A",
                            )}
                          </Typography>
                        </Stack>
                      </Box>

                      <Box
                        sx={{
                          borderRadius: 1,
                          p: 2,
                          maxHeight: 400,
                          overflow: "auto",
                        }}
                      >
                        <RichText content={email.body} />
                      </Box>
                    </Stack>

                    {index < emails.length - 1 && <Divider sx={{ mt: 3 }} />}
                  </Box>
                ))}
              </Stack>
            )}
          </>
        )}
      </DialogContent>
    </MuiDialog>
  );
}
